import React, { useEffect, useMemo, useState } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Tab, TabItem } from '@components/atoms/Tab';
import { SearchInput } from '@components/molecules/SearchInput';
import { Pagination } from '@components/molecules/Pagination';
import UserInsideRow from '@components/molecules/UserInsideRow';
import { useUsersInsideArea } from '@hooks/api/usersInsideArea/useUsersInsideArea';
import {
  UsersInsideAreaFilter,
  UsersInsideAreaPresence,
} from '@models/usersInsideArea.model';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface UsersInsideAreaListProps {
  /** Scope of the query — passing null disables the hook. */
  filter: UsersInsideAreaFilter | null;
  /**
   * Pause polling when the host (drawer / tab) isn't visible. Defaults to
   * `true` so a list embedded directly in a page keeps refreshing.
   */
  enabled?: boolean;
  /** Initial presence — defaults to INSIDE. */
  initialPresence?: UsersInsideAreaPresence;
  /** Reset key — when this changes, page/search/presence reset to defaults. */
  resetKey?: string | number | null;
}

const PAGE_SIZE = 10;

const PRESENCE_TABS: TabItem[] = [
  { id: UsersInsideAreaPresence.ALL, name: 'All' },
  { id: UsersInsideAreaPresence.INSIDE, name: 'Inside now' },
  { id: UsersInsideAreaPresence.PAST, name: 'Past visitors' },
];

export const UsersInsideAreaList: React.FC<UsersInsideAreaListProps> = ({
  filter,
  enabled = true,
  initialPresence = UsersInsideAreaPresence.INSIDE,
  resetKey,
}) => {
  const [page, setPage] = useState(1);
  const [presence, setPresence] = useState<UsersInsideAreaPresence>(initialPresence);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset page/search/presence whenever the host says the scope changed.
  useEffect(() => {
    setPage(1);
    setSearchQuery('');
    setPresence(initialPresence);
    // initialPresence is stable for a given mount; resetKey is the explicit
    // signal — eslint warns but we want exactly this dependency set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Jump to first page on presence change so the user sees the top of the
  // newly-loaded list.
  useEffect(() => {
    setPage(1);
  }, [presence]);

  const effectiveFilter = useMemo<UsersInsideAreaFilter | null>(() => {
    if (!filter) return null;
    return { ...filter, presence };
  }, [filter, presence]);

  const { data: users = [], isLoading, isFetching, refetch, dataUpdatedAt } =
    useUsersInsideArea(effectiveFilter, { enabled });

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.fullName.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q),
    );
  }, [users, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, page]);

  const lastRefreshedLabel = useMemo(() => {
    if (!dataUpdatedAt) return null;
    const date = new Date(dataUpdatedAt);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [dataUpdatedAt]);

  const insideCount = users.filter((u) => u.isInside).length;
  const pastCount = users.length - insideCount;

  const summaryLabel = useMemo(() => {
    const total = filteredUsers.length;
    if (presence === UsersInsideAreaPresence.INSIDE) {
      return `${total} ${total === 1 ? 'person' : 'people'} currently inside`;
    }
    if (presence === UsersInsideAreaPresence.PAST) {
      return `${total} past ${total === 1 ? 'visitor' : 'visitors'}`;
    }
    return `${total} ${total === 1 ? 'person' : 'people'} (${insideCount} inside · ${pastCount} past)`;
  }, [filteredUsers.length, presence, insideCount, pastCount]);

  const emptyLabel = useMemo(() => {
    if (searchQuery.trim()) return 'No users match this search.';
    if (presence === UsersInsideAreaPresence.INSIDE)
      return 'No users currently inside this area.';
    if (presence === UsersInsideAreaPresence.PAST)
      return 'No past visitors recorded for this area.';
    return 'No activity recorded for this area.';
  }, [searchQuery, presence]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 mb-3">
        <Tab
          tabs={PRESENCE_TABS}
          activeTab={presence}
          onTabChange={(id) => setPresence(id as UsersInsideAreaPresence)}
          variant="pills"
          size="sm"
          fullWidth
        />
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name or email..."
          className="w-64"
        />
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          {summaryLabel}
          {lastRefreshedLabel && ` · updated ${lastRefreshedLabel}`}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
        >
          <RefreshIcon
            sx={{ fontSize: 14 }}
            className={isFetching ? 'animate-spin' : undefined}
          />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center text-sm text-gray-500">
          <InfoOutlinedIcon className="text-gray-300 mb-2" sx={{ fontSize: 40 }} />
          <p>{emptyLabel}</p>
          {presence === UsersInsideAreaPresence.INSIDE && !searchQuery.trim() && (
            <p className="text-xs text-gray-400 mt-1">
              Auto-refreshes every 30 seconds while open.
            </p>
          )}
        </div>
      ) : (
        <div>
          <div className="flex flex-col gap-2">
            {paginatedUsers.map((user) => (
              <UserInsideRow key={user.userId} user={user} />
            ))}
          </div>

          {filteredUsers.length > PAGE_SIZE && (
            <Pagination
              page={page}
              take={PAGE_SIZE}
              total={filteredUsers.length}
              setPage={setPage}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default UsersInsideAreaList;
